import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './Admin.css';

// thumbnail รูปบัตร/รูปถือบัตร (โหลด blob ผ่าน token) คลิกเพื่อซูม — img: 'card' | 'face'
function IdCardThumb({ reqId, onZoom, img = 'card' }) {
    const [url, setUrl] = useState('');
    const [err, setErr] = useState(false);
    useEffect(() => {
        let cancelled = false;
        let objectUrl = '';
        (async () => {
            try {
                const params = { variant: 'thumb' };
                if (img === 'face') params.img = 'face';
                const res = await api.get(`/admin/password-resets/${reqId}/id-card`, { params, responseType: 'blob' });
                objectUrl = URL.createObjectURL(res.data);
                if (!cancelled) setUrl(objectUrl); else URL.revokeObjectURL(objectUrl);
            } catch (e) { if (!cancelled) setErr(true); }
        })();
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [reqId, img]);

    if (err) return <span style={{ color: '#dc2626', fontSize: 13 }}>โหลดรูปไม่ได้</span>;
    if (!url) return <span className="idcard-thumb-loading"><span className="spinner-xs"></span></span>;
    return (
        <button type="button" className="idcard-thumb" onClick={() => onZoom(reqId, img)} title="คลิกเพื่อดูขนาดเต็ม">
            <img src={url} alt={img === 'face' ? 'รูปถ่ายถือบัตรประชาชน' : 'บัตรประจำตัวประชาชน'} />
            <span className="idcard-watermark" aria-hidden="true" />
            <span className="idcard-thumb-zoom" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" strokeWidth="2.2">
                    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    <line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
                </svg>
            </span>
        </button>
    );
}

function IdCardLightbox({ reqId, fullname, img = 'card', onClose }) {
    const [url, setUrl] = useState('');
    const [err, setErr] = useState(false);
    useEffect(() => {
        let cancelled = false;
        let objectUrl = '';
        (async () => {
            try {
                const params = img === 'face' ? { img: 'face' } : {};
                const res = await api.get(`/admin/password-resets/${reqId}/id-card`, { params, responseType: 'blob' });
                objectUrl = URL.createObjectURL(res.data);
                if (!cancelled) setUrl(objectUrl); else URL.revokeObjectURL(objectUrl);
            } catch (e) { if (!cancelled) setErr(true); }
        })();
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [reqId, img]);
    return (
        <div className="idcard-lightbox-overlay" onClick={onClose}>
            <div className="idcard-lightbox" onClick={(e) => e.stopPropagation()}>
                <div className="idcard-lightbox-head">
                    <span>{img === 'face' ? 'รูปถ่ายถือบัตรประชาชน' : 'รูปบัตรประชาชน'}{fullname ? ` — ${fullname}` : ''}</span>
                    <button className="idcard-lightbox-close" onClick={onClose} aria-label="ปิด">&times;</button>
                </div>
                <div className="idcard-lightbox-body">
                    {err ? (
                        <p style={{ color: '#fca5a5', padding: 40 }}>ไม่สามารถโหลดรูปได้</p>
                    ) : !url ? (
                        <div className="spinner" />
                    ) : (
                        <div className="idcard-lightbox-imgwrap">
                            <img src={url} alt="บัตรประจำตัวประชาชน" />
                            <span className="idcard-watermark" aria-hidden="true" />
                        </div>
                    )}
                </div>
                <div className="idcard-lightbox-foot">
                    เอกสารนี้เป็นข้อมูลส่วนบุคคล — ใช้เพื่อยืนยันตัวตนในการเปลี่ยนรหัสผ่านเท่านั้น
                </div>
            </div>
        </div>
    );
}

function PasswordResets() {
    const [requests, setRequests] = useState([]);
    const [filter, setFilter] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [lightbox, setLightbox] = useState(null);

    const fetchRequests = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/admin/password-resets', { params: { status: filter } });
            setRequests(res.data || []);
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'โหลดข้อมูลไม่สำเร็จ' });
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { fetchRequests(); }, [fetchRequests]);

    const formatCid = (cid) =>
        cid ? cid.replace(/(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})/, '$1-$2-$3-$4-$5') : '-';
    const formatDate = (d) =>
        d ? new Date(d).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

    const handleApprove = async (req) => {
        const ok = window.confirm(
            `อนุมัติเปลี่ยนรหัสผ่านของ "${req.fullname || '-'}" (${formatCid(req.cid)})?\n\n` +
            `⚠️ ระบบจะเปลี่ยนรหัสผ่านในฐานเงินเดือน (14.11.0.21) และระบบ iMoney ทันที\n` +
            `กรุณาตรวจสอบรูปบัตรประชาชน + เบอร์โทร ว่าตรงกับเจ้าของบัญชีก่อนอนุมัติ`
        );
        if (!ok) return;
        setProcessing(req.id);
        setMessage({ type: '', text: '' });
        try {
            const res = await api.post(`/admin/password-resets/${req.id}/approve`);
            setMessage({ type: 'success', text: res.data?.message || 'เปลี่ยนรหัสผ่านสำเร็จ' });
            fetchRequests();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ดำเนินการไม่สำเร็จ' });
        } finally {
            setProcessing(null);
        }
    };

    const handleReject = async (req) => {
        const reason = window.prompt(`เหตุผลที่ปฏิเสธคำขอของ "${req.fullname || '-'}" (ไม่บังคับ):`, '');
        if (reason === null) return;
        setProcessing(req.id);
        setMessage({ type: '', text: '' });
        try {
            await api.post(`/admin/password-resets/${req.id}/reject`, { admin_note: reason });
            setMessage({ type: 'success', text: 'ปฏิเสธคำขอแล้ว' });
            fetchRequests();
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'ดำเนินการไม่สำเร็จ' });
        } finally {
            setProcessing(null);
        }
    };

    const statusBadge = (status) => {
        const map = {
            pending: { cls: 'warning', text: 'รอดำเนินการ' },
            approved: { cls: 'success', text: 'เปลี่ยนรหัสแล้ว' },
            rejected: { cls: 'danger', text: 'ปฏิเสธ' }
        };
        const b = map[status] || { cls: 'secondary', text: status };
        return <span className={`badge badge-${b.cls}`}>{b.text}</span>;
    };

    const filters = [
        { key: 'pending', label: 'รอดำเนินการ' },
        { key: 'approved', label: 'เปลี่ยนรหัสแล้ว' },
        { key: 'rejected', label: 'ปฏิเสธ' },
        { key: 'all', label: 'ทั้งหมด' }
    ];

    return (
        <div className="admin-page">
            <div className="breadcrumb">
                <Link to="/admin">จัดการระบบ</Link>
                <span>/</span>
                <span>คำขอรีเซ็ตรหัสผ่าน</span>
            </div>

            <div className="admin-header">
                <h1>คำขอรีเซ็ตรหัสผ่าน</h1>
                <p>ตรวจสอบหลักฐาน (รูปบัตร + เบอร์โทร) แล้วอนุมัติเพื่อเปลี่ยนรหัสผ่านให้ผู้ใช้</p>
            </div>

            <div className="alert alert-warning" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', lineHeight: 1.6 }}>
                <span style={{ fontSize: 18, lineHeight: 1 }}>🔒</span>
                <div>
                    <strong>รหัสผ่านใหม่ถูกซ่อนไว้</strong>
                    <div style={{ fontSize: 13, marginTop: 4 }}>
                        ระบบจะไม่แสดงค่ารหัสที่ผู้ใช้ตั้งให้เจ้าหน้าที่เห็น — เมื่อกด “อนุมัติ” ระบบจะเปลี่ยนรหัสในฐานเงินเดือน (14.11.0.21) ให้ทันที
                        โปรดตรวจสอบรูปบัตร + เบอร์โทรให้ตรงกับเจ้าของบัญชีก่อนอนุมัติทุกครั้ง
                    </div>
                </div>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>{message.text}</div>
            )}

            <div className="filter-tabs" style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {filters.map(f => (
                    <button
                        key={f.key}
                        className={`steam-btn ${filter === f.key ? 'steam-btn-primary' : 'steam-btn-secondary'}`}
                        onClick={() => setFilter(f.key)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex-center" style={{ padding: '60px 0' }}>
                    <div className="spinner"></div>
                </div>
            ) : requests.length === 0 ? (
                <div className="steam-card">
                    <div className="steam-card-body" style={{ textAlign: 'center', padding: '48px 0', color: 'var(--steam-text-muted)' }}>
                        ไม่มีคำขอในสถานะนี้
                    </div>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {requests.map(req => (
                        <div key={req.id} className="steam-card">
                            <div className="steam-card-body">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: 240 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                                            <h3 style={{ margin: 0, fontSize: 17 }}>{req.fullname || '(ไม่ทราบชื่อ)'}</h3>
                                            {statusBadge(req.status)}
                                            {req.has_new_password
                                                ? <span className="badge badge-primary">🔑 มีรหัสใหม่ (ซ่อนไว้)</span>
                                                : <span className="badge badge-secondary">ไม่มีรหัสใหม่</span>}
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '6px 20px', fontSize: 14 }}>
                                            <div><span style={{ color: 'var(--steam-text-muted)' }}>เลขบัตร:</span> {formatCid(req.cid)}</div>
                                            <div><span style={{ color: 'var(--steam-text-muted)' }}>เบอร์โทรที่แจ้ง:</span> {req.phone || '-'}</div>
                                            <div><span style={{ color: 'var(--steam-text-muted)' }}>ยื่นเมื่อ:</span> {formatDate(req.created_at)}</div>
                                            <div>
                                                <span style={{ color: 'var(--steam-text-muted)' }}>บัญชีในระบบ:</span>{' '}
                                                {req.has_account
                                                    ? (req.user_last_login
                                                        ? <span style={{ color: '#16a34a', fontWeight: 600 }}>เข้าล่าสุด {formatDate(req.user_last_login)}</span>
                                                        : <span style={{ color: '#d97706', fontWeight: 600 }}>มีบัญชี (ยังไม่เคยเข้า iMoney)</span>)
                                                    : <span style={{ color: 'var(--steam-text-muted)' }}>อยู่ในระบบเงินเดือน</span>}
                                            </div>
                                            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                                                <div>
                                                    <span style={{ color: 'var(--steam-text-muted)', display: 'block', marginBottom: 4 }}>รูปถือบัตร (เทียบหน้า):</span>
                                                    {req.has_face ? (
                                                        <IdCardThumb reqId={req.id} img="face" onZoom={(reqId, img) => setLightbox({ reqId, img, fullname: req.fullname })} />
                                                    ) : (
                                                        <span style={{ color: '#d97706' }}>ไม่มี (คำขอก่อนบังคับแนบ)</span>
                                                    )}
                                                </div>
                                                <div>
                                                    <span style={{ color: 'var(--steam-text-muted)', display: 'block', marginBottom: 4 }}>รูปบัตรประชาชน:</span>
                                                    {req.has_id_card ? (
                                                        <IdCardThumb reqId={req.id} onZoom={(reqId, img) => setLightbox({ reqId, img, fullname: req.fullname })} />
                                                    ) : (
                                                        <span style={{ color: '#dc2626' }}>ไม่มี</span>
                                                    )}
                                                </div>
                                            </div>
                                            {req.note && (
                                                <div style={{ gridColumn: '1 / -1' }}>
                                                    <span style={{ color: 'var(--steam-text-muted)' }}>หมายเหตุ:</span> {req.note}
                                                </div>
                                            )}
                                            {req.status !== 'pending' && (
                                                <div style={{ gridColumn: '1 / -1', color: 'var(--steam-text-muted)' }}>
                                                    ดำเนินการโดย: {req.processed_by_name || '-'} • {formatDate(req.processed_at)}
                                                    {req.admin_note ? ` • หมายเหตุ: ${req.admin_note}` : ''}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {req.status === 'pending' && (
                                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                            <button
                                                className="steam-btn steam-btn-primary"
                                                onClick={() => handleApprove(req)}
                                                disabled={processing === req.id}
                                                title="ตรวจสอบหลักฐานแล้วเปลี่ยนรหัสให้ทันที"
                                            >
                                                {processing === req.id ? '...' : '✅ อนุมัติ & เปลี่ยนรหัส'}
                                            </button>
                                            <button
                                                className="steam-btn steam-btn-danger"
                                                onClick={() => handleReject(req)}
                                                disabled={processing === req.id}
                                            >
                                                ปฏิเสธ
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {lightbox && (
                <IdCardLightbox
                    reqId={lightbox.reqId}
                    fullname={lightbox.fullname}
                    img={lightbox.img || 'card'}
                    onClose={() => setLightbox(null)}
                />
            )}
        </div>
    );
}

export default PasswordResets;
